import { HelpTooltip } from '@front/components/HelpTooltip';
import { useAdmin } from '@front/hook/Api/Admin';
import { useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { useMemberNameById } from '@front/hook/useMemberNameById';
import { usePermissions } from '@front/hook/usePermissions';
import { AdminApiError } from '@front/repository/Api/Admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFrame,
  DialogHeader,
  Loader,
  useToast,
} from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useMemo, useState } from 'react';
import { type TableDefinitionDeleteTarget, TableDefinitionList } from './TableDefinitionList';

export const Route = createFileRoute('/table-definitions/')({
  component: TableDefinitionsIndexPage,
  errorComponent: TableDefinitionsRouteError,
});

function TableDefinitionsRouteError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

/**
 * @feature テーブル定義一覧
 * @capabilities 新規作成への遷移, 詳細表示, 削除, 強制削除（システム管理者）
 * @behavior 「新規作成」→作成画面へ遷移, 行クリック→詳細画面へ遷移, ⋯メニュー「削除」→確認ダイアログ→削除, 「強制削除（管理者）」→確認ダイアログ→未完了タスクをFailed化してテーブル定義を削除。新規作成・削除はプロジェクト管理者、強制削除はシステム管理者のみ
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function TableDefinitionsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { project } = useProject();
  const { isSystemAdmin, isProjectAdmin } = usePermissions();
  const { data: tableDefs, isPending, isFetching } = useImportTableDefinition.List(project.id);
  const { data: sourceSystems } = useSourceSystem.List(project.id);
  const memberNameById = useMemberNameById(project.id);
  const deleteMutation = useImportTableDefinition.Delete();
  const forceRemoveMutation = useAdmin.ForceRemoveImportTableDefinition();

  const [deleteTarget, setDeleteTarget] = useState<TableDefinitionDeleteTarget | null>(null);
  const [forceRemoveTarget, setForceRemoveTarget] = useState<TableDefinitionDeleteTarget | null>(null);

  const sourceSystemNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sourceSystems ?? []) {
      m.set(s.id, s.displayName);
    }
    return m;
  }, [sourceSystems]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ projectId: project.id, id: deleteTarget.id });
      toast.success('削除しました');
      setDeleteTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : '削除に失敗しました';
      toast.error(message);
    }
  };

  const handleForceRemove = async () => {
    if (!forceRemoveTarget) return;
    try {
      const result = await forceRemoveMutation.mutateAsync({ projectId: project.id, id: forceRemoveTarget.id });
      toast.success(`強制削除しました（force-finish: ${result.forceFinishedTaskIds.length} 件）`);
      setForceRemoveTarget(null);
    } catch (err) {
      const isExecutionStillRunning =
        err instanceof AdminApiError &&
        (err.errorCodeId === 'Admin.forceRemoveImportTableDefinition.executionStillRunning' ||
          err.errorCodeId === 'Admin.forceFinishImportTask.executionStillRunning');
      const message = isExecutionStillRunning
        ? 'Cloud Run Job 上の execution がまだ停止していません。停止後に再度実行してください。'
        : err instanceof Error
          ? err.message
          : '強制削除に失敗しました';
      toast.error(message);
    }
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  const rows = tableDefs ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="m-0 text-xl font-semibold leading-none text-default">テーブル定義</h1>
          <HelpTooltip
            content={
              <div className="flex max-w-xs flex-col gap-1 text-left text-xs">
                <p className="font-medium">テーブル定義とは？</p>
                <p>
                  CSV
                  の列と顧客・契約データへのマッピング、一意キー、取込モードなどをまとめた定義です。ソースシステムごとに複数作成できます。
                </p>
              </div>
            }
            side="bottom"
            ariaLabel="テーブル定義の説明"
          />
        </div>
        {isProjectAdmin && (
          <Button variant="primary" size="sm" onClick={() => void navigate({ to: '/table-definitions/new' })}>
            新規作成
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-default bg-subtle-light py-12 text-center text-sm text-muted-light">
          {isProjectAdmin
            ? 'テーブル定義はまだありません。「新規作成」から CSV をアップロードして追加してください。'
            : 'テーブル定義はまだありません。'}
        </p>
      ) : (
        <TableDefinitionList
          rows={rows}
          loading={isFetching}
          sourceSystemNameById={sourceSystemNameById}
          memberNameById={memberNameById}
          isSystemAdmin={isSystemAdmin}
          isProjectAdmin={isProjectAdmin}
          onOpenDetail={(id) => void navigate({ to: '/table-definitions/$id', params: { id: String(id) } })}
          onDelete={setDeleteTarget}
          onForceRemove={setForceRemoveTarget}
        />
      )}

      <Dialog open={forceRemoveTarget !== null} onOpenChange={(open) => !open && setForceRemoveTarget(null)}>
        <DialogContent>
          <DialogFrame>
            <DialogHeader title="テーブル定義を強制削除（管理者）" />
            <DialogBody>
              <div className="flex flex-col gap-2 text-sm text-default">
                <p>
                  「{forceRemoveTarget?.displayName}」を強制削除します。未完了タスク（Requested / Started）は Failed として強制終了され、
                  続けて ImportTableDefinition が論理削除（PII は物理削除）されます。
                </p>
                <p className="text-xs text-muted-light">
                  Cloud Run Job 上の execution が停止していない場合は 409 で失敗します。この操作は取り消せません。
                </p>
              </div>
            </DialogBody>
            <DialogActions>
              <DialogClose>
                <Button variant="basic">キャンセル</Button>
              </DialogClose>
              <Button variant="negative" loading={forceRemoveMutation.isPending} onClick={() => void handleForceRemove()}>
                強制削除する
              </Button>
            </DialogActions>
          </DialogFrame>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogFrame>
            <DialogHeader title="テーブル定義を削除" />
            <DialogBody>
              <p className="text-sm text-default">
                「{deleteTarget?.displayName}」を削除します。この操作は取り消せません。よろしいですか？
              </p>
            </DialogBody>
            <DialogActions>
              <DialogClose>
                <Button variant="basic">キャンセル</Button>
              </DialogClose>
              <Button variant="negative" loading={deleteMutation.isPending} onClick={() => void handleDelete()}>
                削除する
              </Button>
            </DialogActions>
          </DialogFrame>
        </DialogContent>
      </Dialog>
    </div>
  );
}
